import { useState } from 'react';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip, Legend
} from 'recharts';
import ScoreGauge from './ScoreGauge';
import {
  PILLARS, calculateNextIndex, getTier, getScoreColor,
} from '../utils/scoring';
import { formatDate } from '../utils/export';
import WhatIfSimulator from './WhatIfSimulator';
import InteractiveRoadmap from './InteractiveRoadmap';
import GuestJourneyReport from './GuestJourneyReport';
import { getBranding } from './WhiteLabelSettings';

const COMP_COLORS = ['#f87171', '#60a5fa', '#c084fc', '#34d399', '#fb923c', '#22d3ee', '#f472b6', '#facc15', '#a3e635', '#fb7185'];

// All report sections and sub-sections with default visibility
const REPORT_SECTIONS = [
  { id: 'scoreGauge', label: 'Overall Score', default: true, children: [
    { id: 'scoreGauge.gauge', label: 'Score Gauge', default: true },
    { id: 'scoreGauge.tier', label: 'Score Tier Label', default: true },
  ]},
  { id: 'radar', label: 'Category Radar Chart', default: true, children: [
    { id: 'radar.chart', label: 'Radar Chart', default: true },
    { id: 'radar.compLines', label: 'Competitor Lines', default: true },
    { id: 'radar.legend', label: 'Competitor Legend', default: true },
  ]},
  { id: 'scoreGrid', label: 'Pillar Score Grid', default: true },
  { id: 'executive', label: 'Executive Summary', default: true, children: [
    { id: 'executive.diagnostic', label: 'Executive Diagnostic', default: true },
    { id: 'executive.onePage', label: 'Business in One Page', default: true },
  ]},
  { id: 'pillarDetails', label: 'Pillar Details & Rationale', default: true },
  { id: 'strengths', label: 'Strengths & Focus Areas', default: true, children: [
    { id: 'strengths.top', label: 'Top Strengths', default: true },
    { id: 'strengths.focus', label: 'Focus Areas', default: true },
  ]},
  { id: 'scoreTensions', label: 'Score Tensions', default: true },
  { id: 'competitors', label: 'Competitive Position', default: true },
  { id: 'menuTrends', label: 'Menu & Beverage Trends', default: true, children: [
    { id: 'menuTrends.foodAlign', label: 'Food: Trend Alignment', default: true },
    { id: 'menuTrends.foodNotes', label: 'Food: Engineering Notes', default: true },
    { id: 'menuTrends.foodSW', label: 'Food: Strengths/Weaknesses', default: true },
    { id: 'menuTrends.foodTags', label: 'Food: Market Trends Tags', default: true },
    { id: 'menuTrends.bevAlign', label: 'Beverage: Trend Alignment', default: true },
    { id: 'menuTrends.bevNotes', label: 'Beverage: Program Notes', default: true },
    { id: 'menuTrends.bevSW', label: 'Beverage: Strengths/Weaknesses', default: true },
    { id: 'menuTrends.bevTags', label: 'Beverage: Market Trends Tags', default: true },
  ]},
  { id: 'revenueLeaks', label: 'Revenue Leak Map', default: true, children: [
    { id: 'revenueLeaks.items', label: 'Leak Items', default: true },
    { id: 'revenueLeaks.impact', label: 'Impact Levels', default: true },
    { id: 'revenueLeaks.fixes', label: 'Fix Recommendations', default: true },
  ]},
  { id: 'brandCoherence', label: 'Brand Coherence', default: true, children: [
    { id: 'brandCoherence.aiNotes', label: 'AI Analysis', default: true },
    { id: 'brandCoherence.grid', label: 'Heat Map Grid', default: true },
    { id: 'brandCoherence.team', label: 'Team Analysis', default: true },
  ]},
  { id: 'sentiment', label: 'Guest Sentiment', default: true, children: [
    { id: 'sentiment.positive', label: 'Positive Themes', default: true },
    { id: 'sentiment.negative', label: 'Negative Themes', default: true },
  ]},
  { id: 'memoryTest', label: 'The Memory Test', default: true, children: [
    { id: 'memoryTest.moments', label: 'Signature Moments', default: true },
    { id: 'memoryTest.gaps', label: 'Memory Gaps', default: true },
  ]},
  { id: 'externalNotes', label: 'Consultant Notes', default: true },
  { id: 'roadmap', label: 'Next Experience Roadmap', default: true, children: [
    { id: 'roadmap.phase1', label: 'Phase 1 (0-30 days)', default: true },
    { id: 'roadmap.phase2', label: 'Phase 2 (30-90 days)', default: true },
    { id: 'roadmap.phase3', label: 'Phase 3 (90-180 days)', default: true },
  ]},
  { id: 'success', label: 'What Success Looks Like', default: true },
  { id: 'guestJourney', label: 'Guest Journey Report Link', default: true },
  { id: 'signatureMoments', label: 'Signature Moments', default: true },
  { id: 'contentCalendar', label: '30-Day Content Calendar', default: true },
  { id: 'whatIf', label: 'What-If Simulator', default: false },
  { id: 'photos', label: 'Photos', default: false },
];

export default function ClientView({ audit, isInternal = false, onSaveSections, onUpdateAudit }) {
  if (!audit) return <div className="text-center py-20 text-neutral-500">No audit data</div>;

  const scores = audit.scores || {};
  const intake = audit.intake || {};
  const nextIndex = calculateNextIndex(scores);
  const competitors = Array.isArray(audit.approvedCompetitors) ? audit.approvedCompetitors : Array.isArray(audit.marketData?.competitors) ? audit.marketData.competitors : [];
  const ai = audit.aiResearch || {};
  const brand = getBranding(audit);

  // Section visibility — build defaults from all sections + children
  const buildDefaults = () => {
    const defaults = {};
    REPORT_SECTIONS.forEach(s => {
      defaults[s.id] = s.default;
      if (s.children) s.children.forEach(c => { defaults[c.id] = c.default; });
    });
    return defaults;
  };

  const [sections, setSections] = useState({ ...buildDefaults(), ...(audit.reportSections || {}) });
  const [showControls, setShowControls] = useState(false);
  const [activeView, setActiveView] = useState('overview');

  // Client nav: which sections belong to which view
  const viewMap = {
    overview: ['scoreGauge', 'radar', 'scoreGrid', 'executive', 'strengths'],
    analysis: ['pillarDetails', 'scoreTensions', 'menuTrends', 'brandCoherence', 'sentiment', 'memoryTest'],
    competitive: ['competitors'],
    operations: ['revenueLeaks', 'externalNotes'],
    journey: ['guestJourneyEmbed'],
    roadmap: ['roadmap', 'success'],
    ideation: ['signatureMoments', 'contentCalendar', 'whatIf', 'photos'],
  };
  const inView = (sectionId) => isInternal || viewMap[activeView]?.includes(sectionId);

  const show = (id) => sections[id] === true || sections[id] === undefined;
  const toggleSection = (id) => {
    const current = sections[id] !== false;
    const updated = { ...sections, [id]: !current };

    // If toggling a parent off, also toggle all children off
    const parent = REPORT_SECTIONS.find(s => s.id === id);
    if (parent?.children && current) {
      parent.children.forEach(c => { updated[c.id] = false; });
    }
    // If toggling a parent on, also toggle all children on
    if (parent?.children && !current) {
      parent.children.forEach(c => { updated[c.id] = true; });
    }

    setSections(updated);
    if (onSaveSections) onSaveSections(updated);
  };

  // Build radar data
  const radarData = PILLARS.map((p, pIdx) => {
    const entry = { pillar: p.name.length > 15 ? p.name.substring(0, 14) + '...' : p.name, fullName: p.name, score: scores[p.id]?.score || 0 };
    competitors.slice(0, 10).forEach((comp, ci) => {
      const myScore = scores[p.id]?.score || 5;
      const reviewScore = comp.reviewScore || 0;
      const base = reviewScore > 0 ? reviewScore * 2 : 5;
      const compNameLen = (comp.name || '').length;
      const offset = ((compNameLen * 3 + pIdx * 7) % 20 - 10) * 0.15;
      let contextBonus = 0;
      const pillarId = p.id || '';
      if (comp.strengths && pillarId.split('_').some(w => (comp.strengths || '').toLowerCase().includes(w))) contextBonus = 0.6;
      if (comp.weaknesses && pillarId.split('_').some(w => (comp.weaknesses || '').toLowerCase().includes(w))) contextBonus = -0.6;
      entry[`comp_${ci}`] = Math.max(1, Math.min(10, Math.round((base + offset + contextBonus) * 10) / 10));
    });
    return entry;
  });

  const sorted = [...PILLARS].map(p => ({ ...p, score: scores[p.id]?.score || 0 })).sort((a, b) => b.score - a.score);
  const externalNotes = (audit.comments || []).filter(c => (c.visibility || 'external') === 'external');
  const notesBySection = {};
  externalNotes.forEach(n => { if (!notesBySection[n.section]) notesBySection[n.section] = []; notesBySection[n.section].push(n); });

  const roadmap = ai.roadmap ? [
    { phase: '0–30 Days', label: 'Quick Wins', color: '#34d399', items: ai.roadmap.immediate || [] },
    { phase: '30–90 Days', label: 'Strategic Build', color: '#fbbf24', items: ai.roadmap.shortTerm || [] },
    { phase: '90–180 Days', label: 'Scale & Sustain', color: '#C9A668', items: ai.roadmap.mediumTerm || [] },
  ] : [
    { phase: '0–30 Days', label: 'Quick Wins', color: '#34d399', items: sorted.slice(-2).map(p => `Improve ${p.name}`) },
    { phase: '30–90 Days', label: 'Strategic Build', color: '#fbbf24', items: ['Strengthen brand coherence', 'Develop signature guest moments'] },
    { phase: '90–180 Days', label: 'Scale & Sustain', color: '#C9A668', items: ['Re-audit and measure progress', 'Build scalable growth systems'] },
  ];

  return (
    <div id="client-report" className="min-h-screen">
      {/* Section toggle controls — collapsible, only visible to internal users */}
      {isInternal && (
        <div className="card mb-6 overflow-hidden">
          <button
            onClick={() => setShowControls(!showControls)}
            className="w-full flex items-center justify-between p-4 hover:bg-neutral-800/30 transition-colors"
          >
            <span className="text-xs text-brand-gold font-semibold uppercase tracking-wider">Report Sections</span>
            <span className="text-neutral-500 text-xs">{showControls ? '▲ Collapse' : '▼ Configure what appears in report'}</span>
          </button>
          {showControls && (
            <div className="px-4 pb-4 space-y-2 border-t border-neutral-800">
              <div className="pt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {REPORT_SECTIONS.map(s => (
                  <div key={s.id}>
                    <label className="flex items-center gap-2 text-xs text-neutral-300 cursor-pointer hover:text-white transition-colors font-medium py-0.5">
                      <input type="checkbox" checked={sections[s.id] !== false} onChange={() => toggleSection(s.id)} className="rounded border-neutral-600 bg-neutral-800 text-brand-gold focus:ring-brand-gold/30" />
                      {s.label}
                    </label>
                    {s.children && sections[s.id] !== false && (
                      <div className="ml-6 mt-0.5 space-y-0.5">
                        {s.children.map(sub => (
                          <label key={sub.id} className="flex items-center gap-1.5 text-[11px] text-neutral-500 cursor-pointer hover:text-neutral-300 transition-colors py-0.5">
                            <input type="checkbox" checked={sections[sub.id] !== false} onChange={() => toggleSection(sub.id)} className="rounded border-neutral-700 bg-neutral-800 text-brand-gold focus:ring-brand-gold/30 w-3 h-3" />
                            {sub.label}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Hero */}
      <div className="bg-gradient-to-br from-black via-neutral-900 to-neutral-800 px-4 py-10 sm:px-12 sm:py-16 text-center">
        <div className="flex items-center justify-center gap-3 mb-4 sm:mb-6">
          <img src={brand.logo} alt={brand.name} className="h-8 sm:h-10 w-auto max-w-[140px] sm:max-w-[160px] object-contain" />
          {audit.branding?.source && audit.branding.source !== 'negroup' && (
            <span className="font-bold text-white text-sm">{brand.name}</span>
          )}
        </div>
        <h1 className="font-display text-2xl sm:text-5xl text-white mb-2">{intake?.conceptName}</h1>
        <p className="text-neutral-400 text-xs sm:text-sm mb-6 sm:mb-8">{intake?.city} — Business Health Audit</p>
        {show('scoreGauge') && show('scoreGauge.gauge') && <ScoreGauge score={nextIndex} size={typeof window !== 'undefined' && window.innerWidth < 640 ? 150 : 200} />}
        {show('scoreGauge') && show('scoreGauge.tier') && (
          <p className="text-neutral-400 text-xs sm:text-sm mt-3">{getTier(nextIndex).name}</p>
        )}
      </div>

      {/* Client Navigation */}
      {!isInternal && (
        <div className="sticky top-0 z-40 bg-black/90 backdrop-blur-sm border-b border-neutral-800">
          <div className="max-w-4xl mx-auto px-4 overflow-x-auto scrollbar-hide">
            <div className="flex gap-1 py-2 min-w-max">
              {[
                { id: 'overview', label: 'Overview', hasContent: true },
                { id: 'analysis', label: 'Analysis', hasContent: Object.keys(scores).length > 0 },
                { id: 'competitive', label: 'Competitive', hasContent: competitors.length > 0 && show('competitors') },
                { id: 'operations', label: 'Operations', hasContent: (ai.revenueLeaks?.length > 0 && show('revenueLeaks')) || (externalNotes.length > 0 && show('externalNotes')) },
                { id: 'roadmap', label: 'Roadmap', hasContent: show('roadmap') },
                { id: 'ideation', label: 'Ideation', hasContent: (Array.isArray(audit.signatureMoments) && audit.signatureMoments.length > 0 && show('signatureMoments')) || (Array.isArray(audit.contentCalendar) && audit.contentCalendar.length > 0 && show('contentCalendar')) },
                { id: 'journey', label: 'Visit Audit', hasContent: audit.guestJourney && Object.keys(audit.guestJourney).length > 0 },
              ].filter(nav => nav.hasContent).map(nav => (
                <button
                  key={nav.id}
                  onClick={() => setActiveView(nav.id)}
                  className={`whitespace-nowrap px-4 py-2.5 sm:py-2 rounded-lg text-sm sm:text-xs font-medium transition-all active:scale-95 ${
                    activeView === nav.id
                      ? 'bg-brand-gold/15 text-brand-gold'
                      : 'text-neutral-500 hover:text-neutral-300'
                  }`}
                >
                  {nav.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">

        {inView('radar') && show('radar') && (
          <div className="mb-10">
            <h2 className="font-display text-xl text-white mb-4 text-center">Category Breakdown</h2>
            <div className="card p-6">
              {show('radar.legend') && competitors.length > 0 && (
                <div className="flex flex-wrap items-center justify-center gap-3 mb-4">
                  <span className="flex items-center gap-1.5 text-[11px] text-brand-gold font-medium"><span className="w-2.5 h-2.5 rounded-full bg-brand-gold" />{intake?.conceptName}</span>
                  {competitors.slice(0, 10).map((comp, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-[11px]" style={{ color: COMP_COLORS[i] }}><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COMP_COLORS[i] }} />{comp.name}</span>
                  ))}
                </div>
              )}
              {show('radar.chart') && <div className="h-[280px] sm:h-[380px]">
                <ResponsiveContainer>
                  <RadarChart cx="50%" cy="50%" outerRadius="75%" data={radarData}>
                    <PolarGrid stroke="rgba(255,255,255,0.1)" />
                    <PolarAngleAxis dataKey="pillar" tick={{ fill: '#a3a3a3', fontSize: 10 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#525252', fontSize: 9 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#171717', border: '1px solid #333', borderRadius: 8, fontSize: 12 }} />
                    <Radar name={intake?.conceptName || 'This Property'} dataKey="score" stroke="#C9A668" fill="#C9A668" fillOpacity={0.25} strokeWidth={2} />
                    {show('radar.compLines') && competitors.slice(0, 10).map((comp, i) => (
                      <Radar key={i} name={comp.name} dataKey={`comp_${i}`} stroke={COMP_COLORS[i]} fill={COMP_COLORS[i]} fillOpacity={0.08} strokeWidth={1.5} />
                    ))}
                  </RadarChart>
                </ResponsiveContainer>
              </div>}
            </div>
          </div>
        )}

        {/* Score Grid */}
        {inView('scoreGrid') && show('scoreGrid') && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-10">
            {PILLARS.map(p => {
              const s = scores[p.id]?.score || 0;
              const color = getScoreColor(s);
              return (
                <div key={p.id} className={`card p-3 text-center ${color.border} border`}>
                  <p className={`text-2xl font-black ${color.text}`}>{s.toFixed(1)}</p>
                  <p className="text-[10px] text-neutral-400 mt-1 leading-tight">{p.name}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Executive Summary */}
        {inView('executive') && show('executive') && (ai.executiveDiagnostic || ai.businessInOnePage) && (
          <div className="card p-6 mb-8 border-l-4 border-brand-gold">
            <h2 className="font-display text-lg text-brand-gold mb-3">Executive Summary</h2>
            {show('executive.diagnostic') && ai.executiveDiagnostic && <p className="text-neutral-300 text-sm leading-relaxed">{ai.executiveDiagnostic}</p>}
            {show('executive.onePage') && ai.businessInOnePage && <p className="text-neutral-400 text-sm mt-3 leading-relaxed">{ai.businessInOnePage}</p>}
          </div>
        )}

        {/* Guest Journey Report — embedded in Journey tab */}
        {inView('guestJourneyEmbed') && audit.guestJourney && Object.keys(audit.guestJourney).length > 0 && (
          <div className="mb-8">
            <GuestJourneyReport audit={audit} embedded={true} />
          </div>
        )}

        {/* Pillar Details */}
        {inView('pillarDetails') && show('pillarDetails') && (
          <div className="mb-10">
            <h2 className="font-display text-xl text-white mb-4">Pillar Analysis</h2>
            <div className="space-y-3">
              {PILLARS.map(p => {
                const data = scores[p.id];
                if (!data || data.score === 0) return null;
                const color = getScoreColor(data.score);
                return (
                  <div key={p.id} className="card p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`text-lg font-black ${color.text}`}>{data.score.toFixed(1)}</span>
                      <span className="text-sm font-semibold text-white">{p.name}</span>
                    </div>
                    {data.rationale && <p className="text-sm text-neutral-400 leading-relaxed">{data.rationale}</p>}
                    {isInternal && data.confidence && <p className="text-[10px] text-neutral-600 mt-1">Confidence: {data.confidence} | Evidence: {data.evidence || 'N/A'}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Strengths & Focus Areas */}
        {inView('strengths') && show('strengths') && (
          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            {show('strengths.top') && (
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-emerald-400 mb-3">Top Strengths</h3>
                {sorted.slice(0, 3).map(p => (
                  <div key={p.id} className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="text-sm text-neutral-300">{p.name}</span>
                    <span className="text-sm font-bold text-emerald-400 ml-auto">{p.score.toFixed(1)}</span>
                  </div>
                ))}
              </div>
            )}
            {show('strengths.focus') && (
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-red-400 mb-3">Focus Areas</h3>
                {sorted.slice(-3).reverse().map(p => (
                  <div key={p.id} className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-red-400" />
                    <span className="text-sm text-neutral-300">{p.name}</span>
                    <span className="text-sm font-bold text-red-400 ml-auto">{p.score.toFixed(1)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Score Tensions */}
        {inView('scoreTensions') && show('scoreTensions') && ai.scoreTensions?.length > 0 && (
          <div className="card p-5 mb-10">
            <h2 className="font-display text-lg text-white mb-3">Score Tensions</h2>
            {ai.scoreTensions.filter(Boolean).map((t, i) => (
              <p key={i} className="text-sm text-neutral-400 mb-2 flex items-start gap-2"><span className="text-amber-400 mt-0.5">!</span> {typeof t === 'string' ? t : t.tension || t.description || JSON.stringify(t)}</p>
            ))}
          </div>
        )}

        {/* Competitive Position */}
        {inView('competitors') && show('competitors') && competitors.length > 0 && (
          <div className="mb-10">
            <h2 className="font-display text-xl text-white mb-4">Competitive Position</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {competitors.slice(0, 10).map((comp, i) => (
                <div key={i} className="card p-4" style={{ borderLeftWidth: 3, borderLeftColor: COMP_COLORS[i] }}>
                  <p className="font-semibold text-white text-sm mb-1">{comp.name}</p>
                  <p className="text-neutral-500 text-xs mb-2">{comp.type}{comp.priceRange ? ` · ${comp.priceRange}` : ''}</p>
                  {comp.strengths && <p className="text-xs text-emerald-400/80 mb-1">+ {comp.strengths}</p>}
                  {comp.weaknesses && <p className="text-xs text-red-400/80">- {comp.weaknesses}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Menu & Beverage Trends */}
        {inView('menuTrends') && show('menuTrends') && (() => {
          const ma = audit.menuAnalysis || ai.menuAnalysis;
          const ba = audit.beverageAnalysis || ai.beverageAnalysis;
          if (!ma && !ba) return null;
          return (
            <div className="card p-6 mb-10">
              <h2 className="font-display text-lg text-white mb-4">Menu & Beverage Intelligence</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                {ma && (
                  <div>
                    <h4 className="text-sm font-semibold text-brand-gold mb-2">Food & Menu</h4>
                    {show('menuTrends.foodAlign') && ma.trendAlignment && <p className="text-xs text-neutral-400 mb-2">{ma.trendAlignment}</p>}
                    {show('menuTrends.foodNotes') && ma.engineeringNotes && <p className="text-xs text-neutral-400 mb-2">{ma.engineeringNotes}</p>}
                    {show('menuTrends.foodSW') && ma.strengths?.filter(Boolean).map((s, i) => <p key={i} className="text-xs text-emerald-400/80 mb-1">+ {s}</p>)}
                    {show('menuTrends.foodSW') && ma.weaknesses?.filter(Boolean).map((w, i) => <p key={i} className="text-xs text-red-400/80 mb-1">- {w}</p>)}
                    {show('menuTrends.foodTags') && audit.marketData?.menuTrends?.food?.filter(Boolean).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">{audit.marketData.menuTrends.food.filter(Boolean).map((t, i) => (
                        <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-brand-gold/10 text-brand-gold border border-brand-gold/20">{t}</span>
                      ))}</div>
                    )}
                  </div>
                )}
                {ba && (
                  <div>
                    <h4 className="text-sm font-semibold text-brand-teal mb-2">Beverage Program</h4>
                    {show('menuTrends.bevAlign') && ba.trendAlignment && <p className="text-xs text-neutral-400 mb-2">{ba.trendAlignment}</p>}
                    {show('menuTrends.bevNotes') && ba.programNotes && <p className="text-xs text-neutral-400 mb-2">{ba.programNotes}</p>}
                    {show('menuTrends.bevSW') && ba.strengths?.filter(Boolean).map((s, i) => <p key={i} className="text-xs text-emerald-400/80 mb-1">+ {s}</p>)}
                    {show('menuTrends.bevSW') && ba.weaknesses?.filter(Boolean).map((w, i) => <p key={i} className="text-xs text-red-400/80 mb-1">- {w}</p>)}
                    {show('menuTrends.bevTags') && audit.marketData?.menuTrends?.beverage?.filter(Boolean).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">{audit.marketData.menuTrends.beverage.filter(Boolean).map((t, i) => (
                        <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-brand-teal/10 text-brand-teal border border-brand-teal/20">{t}</span>
                      ))}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Revenue Leaks */}
        {inView('revenueLeaks') && show('revenueLeaks') && ai.revenueLeaks?.length > 0 && (
          <div className="card p-6 mb-10">
            <h2 className="font-display text-lg text-white mb-4">Revenue Leak Map</h2>
            <div className="space-y-2">
              {ai.revenueLeaks.filter(l => l.area).map((leak, i) => (
                <div key={i} className="flex items-start gap-3 bg-neutral-800/30 rounded-lg p-3">
                  {show('revenueLeaks.impact') && <span className={`text-xs px-2 py-0.5 rounded-full font-medium mt-0.5 ${leak.impact === 'High' ? 'bg-red-400/20 text-red-400' : leak.impact === 'Medium' ? 'bg-amber-400/20 text-amber-400' : 'bg-neutral-700 text-neutral-400'}`}>{leak.impact}</span>}
                  <div>
                    {show('revenueLeaks.items') && <><p className="text-sm text-white font-medium">{leak.area}</p><p className="text-xs text-neutral-400">{leak.issue}</p></>}
                    {show('revenueLeaks.fixes') && leak.fix && <p className="text-xs text-emerald-400/80 mt-1">Fix: {leak.fix}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Brand Coherence */}
        {inView('brandCoherence') && show('brandCoherence') && (
          <div className="card p-5 mb-10">
            <h2 className="font-display text-lg text-white mb-3">Brand Coherence</h2>
            {show('brandCoherence.aiNotes') && (audit.brandCoherence?.overallNotes || ai.brandCoherence?.overallNotes) && (
              <p className="text-sm text-neutral-400 leading-relaxed mb-3">{audit.brandCoherence?.overallNotes || ai.brandCoherence?.overallNotes}</p>
            )}
            {show('brandCoherence.aiNotes') && (audit.brandCoherence?.websiteAlign || audit.brandCoherence?.menuAlign || audit.brandCoherence?.socialAlign || audit.brandCoherence?.reviewAlign) && (
              <div className="bg-neutral-800/30 rounded-lg p-3 mb-3 space-y-1">
                {audit.brandCoherence?.websiteAlign && <p className="text-xs text-neutral-400"><span className="text-neutral-300 font-medium">Website:</span> {audit.brandCoherence.websiteAlign}</p>}
                {audit.brandCoherence?.menuAlign && <p className="text-xs text-neutral-400"><span className="text-neutral-300 font-medium">Menu:</span> {audit.brandCoherence.menuAlign}</p>}
                {audit.brandCoherence?.socialAlign && <p className="text-xs text-neutral-400"><span className="text-neutral-300 font-medium">Social:</span> {audit.brandCoherence.socialAlign}</p>}
                {audit.brandCoherence?.reviewAlign && <p className="text-xs text-neutral-400"><span className="text-neutral-300 font-medium">Reviews:</span> {audit.brandCoherence.reviewAlign}</p>}
              </div>
            )}
            {show('brandCoherence.grid') && audit.brandCoherence?._grid && (() => {
              const grid = audit.brandCoherence._grid;
              const channels = ['website', 'menu', 'social', 'inVenue', 'reviews'];
              const channelNames = { website: 'Website', menu: 'Menu', social: 'Social', inVenue: 'In-Venue', reviews: 'Reviews' };
              const dims = ['Tone', 'Visual Identity', 'Key Message', 'Value Proposition', 'Personality'];
              const colorMap = { aligned: 'bg-emerald-500/40', partial: 'bg-amber-500/40', fractured: 'bg-red-500/30', neutral: 'bg-neutral-700/30' };
              return (
                <div className="overflow-x-auto mb-3">
                  <table className="w-full text-xs">
                    <thead><tr><th className="text-left text-neutral-500 pb-2 pr-3"></th>
                      {dims.map(d => <th key={d} className="text-center text-neutral-500 pb-2 px-1">{d}</th>)}
                    </tr></thead>
                    <tbody>{channels.map(ch => (
                      <tr key={ch}><td className="text-neutral-300 py-1 pr-3">{channelNames[ch] || ch}</td>
                        {dims.map(dim => {
                          const val = grid[ch]?.[dim] || 'neutral';
                          return <td key={dim} className="text-center py-1 px-1"><div className={`w-full h-6 rounded ${colorMap[val] || colorMap.neutral}`} /></td>;
                        })}
                      </tr>
                    ))}</tbody>
                  </table>
                  <div className="flex gap-3 mt-2 text-[10px] text-neutral-500">
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-500/40" />Aligned</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-500/40" />Partial</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-500/30" />Fractured</span>
                  </div>
                </div>
              );
            })()}
            {show('brandCoherence.team') && audit.brandCoherence?.teamAnalysis && (
              <div className="border-t border-neutral-800 pt-3 mt-3">
                <p className="text-xs text-brand-gold font-semibold mb-1 uppercase tracking-wider">Team Analysis</p>
                <p className="text-sm text-neutral-300 leading-relaxed">{audit.brandCoherence.teamAnalysis}</p>
              </div>
            )}
          </div>
        )}

        {/* Guest Sentiment */}
        {inView('sentiment') && show('sentiment') && ai.guestSentiment && (() => {
          const posThemes = ai.guestSentiment.combined?.positiveThemes || ai.guestSentiment.positiveThemes || ai.guestSentiment.google?.positiveThemes || [];
          const negThemes = ai.guestSentiment.combined?.negativeThemes || ai.guestSentiment.negativeThemes || ai.guestSentiment.google?.negativeThemes || [];
          if (posThemes.filter(Boolean).length === 0 && negThemes.filter(Boolean).length === 0) return null;
          return (
            <div className="card p-6 mb-10">
              <h2 className="font-display text-lg text-white mb-4">Guest Sentiment</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {show('sentiment.positive') && posThemes.filter(Boolean).length > 0 && (
                  <div>
                    <p className="text-xs text-emerald-400 font-semibold mb-2">Positive Themes</p>
                    {posThemes.filter(Boolean).map((t, i) => <p key={i} className="text-xs text-neutral-400 mb-1">+ {t}</p>)}
                  </div>
                )}
                {show('sentiment.negative') && negThemes.filter(Boolean).length > 0 && (
                  <div>
                    <p className="text-xs text-red-400 font-semibold mb-2">Negative Themes</p>
                    {negThemes.filter(Boolean).map((t, i) => <p key={i} className="text-xs text-neutral-400 mb-1">- {t}</p>)}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Memory Test */}
        {inView('memoryTest') && show('memoryTest') && (audit.memoryTest || ai.memoryTest) && (
          <div className="card p-5 mb-10">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-display text-lg text-white">The Memory Test</h2>
                <p className="text-xs text-neutral-500">What would a guest remember 48 hours later?</p>
              </div>
              {(audit.memoryTest?.score || ai.memoryTest?.score) && (
                <div className={`w-12 h-12 rounded-xl ${getScoreColor(audit.memoryTest?.score || ai.memoryTest?.score || 0).bg} flex items-center justify-center`}>
                  <span className={`text-lg font-black ${getScoreColor(audit.memoryTest?.score || ai.memoryTest?.score || 0).text}`}>{(audit.memoryTest?.score || ai.memoryTest?.score || 0).toFixed(1)}</span>
                </div>
              )}
            </div>
            {show('memoryTest.moments') && (audit.memoryTest?.signatureMoments || ai.memoryTest?.signatureMoments || []).filter(Boolean).length > 0 && (
              <div className="mb-3">
                <p className="text-xs text-emerald-400 font-semibold mb-1.5">Signature Moments</p>
                {(audit.memoryTest?.signatureMoments || ai.memoryTest?.signatureMoments).filter(Boolean).map((m, i) => <p key={i} className="text-xs text-neutral-400 mb-1">+ {m}</p>)}
              </div>
            )}
            {show('memoryTest.gaps') && (audit.memoryTest?.gaps || ai.memoryTest?.gaps || []).filter(Boolean).length > 0 && (
              <div>
                <p className="text-xs text-red-400 font-semibold mb-1.5">Memory Gaps</p>
                {(audit.memoryTest?.gaps || ai.memoryTest?.gaps).filter(Boolean).map((g, i) => <p key={i} className="text-xs text-neutral-400 mb-1">- {g}</p>)}
              </div>
            )}
          </div>
        )}

        {/* External Notes */}
        {inView('externalNotes') && show('externalNotes') && (
          <div className="mb-10">
            <h2 className="font-display text-xl text-white mb-4">Consultant Notes</h2>
            {externalNotes.length > 0 ? Object.entries(notesBySection).map(([section, notes]) => (
              <div key={section} className="mb-4">
                <p className="text-xs text-brand-gold font-semibold mb-2 uppercase tracking-wider">{section}</p>
                {notes.map(n => (
                  <div key={n.id} className="card p-3 mb-2">
                    <p className="text-sm text-neutral-300">{n.text}</p>
                    <p className="text-[10px] text-neutral-600 mt-1">{n.author}</p>
                  </div>
                ))}
              </div>
            )) : <p className="text-neutral-600 text-sm">No external notes yet.</p>}
          </div>
        )}

        {/* What-If Simulator */}
        {inView('whatIf') && show('whatIf') && (
          <div className="mb-10">
            <WhatIfSimulator scores={scores} currentIndex={nextIndex} />
          </div>
        )}

        {/* Photos */}
        {inView('photos') && show('photos') && (
          <div className="mb-10">
            <h2 className="font-display text-xl text-white mb-4">Photos</h2>
            {(audit.photos || []).length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {audit.photos.map(p => (
                  <div key={p.id}>
                    <img src={p.dataUrl} alt={p.caption} className="w-full h-40 object-cover rounded-lg" />
                    {p.caption && <p className="text-xs text-neutral-500 mt-1">{p.caption}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-neutral-600 text-sm">No photos attached to this audit.</p>
            )}
          </div>
        )}

        {/* Interactive Roadmap */}
        {inView('roadmap') && show('roadmap') && (
          <div className="mb-10">
            <InteractiveRoadmap
              audit={audit}
              onUpdate={(updated) => {
                if (onUpdateAudit) onUpdateAudit(updated);
              }}
              isClientView={!isInternal}
              visiblePhases={[show('roadmap.phase1'), show('roadmap.phase2'), show('roadmap.phase3')]}
            />
          </div>
        )}

        {/* What Success Looks Like */}
        {inView('success') && show('success') && ai.whatSuccessLooksLike && (
          <div className="card p-6 mb-10 border-l-4 border-brand-gold">
            <h2 className="font-display text-lg text-brand-gold mb-3">What Success Looks Like</h2>
            <p className="text-sm text-neutral-300 leading-relaxed">{ai.whatSuccessLooksLike}</p>
          </div>
        )}

        {/* Signature Moments — from Ideation tab (rich objects) or Memory Test (strings) */}
        {inView('signatureMoments') && show('signatureMoments') && (() => {
          const richMoments = Array.isArray(audit.signatureMoments) ? audit.signatureMoments.filter(m => m && (m.title || m.description)) : [];
          const simpleMoments = (audit.memoryTest?.signatureMoments || ai.memoryTest?.signatureMoments || []).filter(Boolean);
          if (richMoments.length === 0 && simpleMoments.length === 0) return null;
          return (
            <div className="card p-6 mb-10">
              <h2 className="font-display text-lg text-brand-gold mb-4">Signature Moments</h2>
              <p className="text-xs text-neutral-500 mb-4">"Only here" moments that create memories and differentiation</p>
              {richMoments.length > 0 ? (
                <div className="space-y-4">
                  {richMoments.map((m, i) => (
                    <div key={i} className="bg-neutral-800/20 border border-neutral-800 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-white">{m.title}</h3>
                        {m.difficulty && <span className={`text-[9px] px-2 py-0.5 rounded font-medium ${m.difficulty === 'Easy' ? 'bg-emerald-400/20 text-emerald-400' : m.difficulty === 'Medium' ? 'bg-amber-400/20 text-amber-400' : 'bg-red-400/20 text-red-400'}`}>{m.difficulty}</span>}
                      </div>
                      {m.description && <p className="text-xs text-neutral-300 mb-2">{m.description}</p>}
                      {m.why && <p className="text-xs text-neutral-500 italic mb-1">Why: {m.why}</p>}
                      {m.revenueImpact && <p className="text-xs text-brand-gold">Revenue Impact: {m.revenueImpact}</p>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {simpleMoments.map((m, i) => (
                    <p key={i} className="text-xs text-neutral-300">+ {typeof m === 'string' ? m : m.title || JSON.stringify(m)}</p>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* 30-Day Content Calendar — both calendar grid and list view */}
        {inView('contentCalendar') && show('contentCalendar') && Array.isArray(audit.contentCalendar) && audit.contentCalendar.length > 0 && (() => {
          const cal = audit.contentCalendar;
          const typeColors = {
            'Behind the Scenes': '#C9A668', 'Food Shot': '#fb923c', 'Cocktail Feature': '#c084fc',
            'Team Spotlight': '#60a5fa', 'Guest Story': '#34d399', 'Seasonal': '#fbbf24',
            'Event': '#f87171', 'Community': '#06b6d4', 'Reel Idea': '#e040fb', 'Story Series': '#a78bfa',
          };
          const weeks = [];
          for (let i = 0; i < cal.length; i += 7) weeks.push(cal.slice(i, i + 7));
          const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
          return (
            <div className="card p-6 mb-10">
              <h2 className="font-display text-lg text-brand-gold mb-4">30-Day Content Calendar</h2>

              {/* Calendar Grid View */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-3">Calendar View</h3>
                <div className="space-y-3">
                  {weeks.map((week, wi) => (
                    <div key={wi}>
                      <p className="text-[10px] text-neutral-600 mb-1">Week {wi + 1}</p>
                      <div className="grid grid-cols-7 gap-1">
                        {wi === 0 && DAYS_OF_WEEK.map(d => (
                          <div key={d} className="text-[8px] text-neutral-600 text-center pb-1">{d}</div>
                        ))}
                        {week.map((post, di) => (
                          <div key={di} className="bg-neutral-800/30 rounded p-1.5 min-h-[60px]" title={post.caption}>
                            <div className="w-full h-1 rounded-full mb-1" style={{ backgroundColor: typeColors[post.type] || '#666' }} />
                            <p className="text-[8px] text-white font-medium leading-tight truncate">{post.title}</p>
                            <p className="text-[7px] text-neutral-600 mt-0.5">{post.type}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* List View */}
              <div>
                <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-3">Detailed List</h3>
                <div className="space-y-2">
                  {cal.map((post, i) => (
                    <div key={i} className="bg-neutral-800/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs font-bold text-neutral-500 w-8">D{post.day || i + 1}</span>
                        {post.type && <span className="text-[9px] px-2 py-0.5 rounded font-medium" style={{ backgroundColor: (typeColors[post.type] || '#666') + '20', color: typeColors[post.type] || '#666' }}>{post.type}</span>}
                        {post.timing && <span className="text-[9px] text-neutral-600">{post.timing}</span>}
                        {post.pillarLink && <span className="text-[8px] text-neutral-700 ml-auto">{post.pillarLink}</span>}
                      </div>
                      <p className="text-xs text-white font-medium mb-1">{post.title}</p>
                      {post.caption && <p className="text-[10px] text-neutral-400 leading-relaxed">{post.caption}</p>}
                      {post.hashtags?.filter(Boolean).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {post.hashtags.filter(Boolean).map((tag, j) => (
                            <span key={j} className="text-[9px] text-brand-gold/60">#{tag.replace('#', '')}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Content type legend */}
              <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-neutral-800">
                {Object.entries(typeColors).map(([type, color]) => {
                  const count = cal.filter(p => p.type === type).length;
                  if (count === 0) return null;
                  return (
                    <span key={type} className="flex items-center gap-1 text-[9px]" style={{ color }}>
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />{type} ({count})
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Footer */}
        <div className="text-center pt-6 border-t border-neutral-800">
          {brand.showPoweredBy && (
            <div className="flex items-center justify-center gap-2 mb-2">
              <img src="https://static.wixstatic.com/media/cf458d_f7329a8f179a4571a1951c4ed9431604~mv2.png" alt="Next Experience Group" className="h-5 w-auto opacity-60" />
              <span className="text-[10px] text-neutral-600">Powered by Next Experience Group</span>
            </div>
          )}
          {!brand.showPoweredBy && (
            <div className="flex items-center justify-center gap-2 mb-2">
              <img src={brand.logo} alt={brand.company || brand.name} className="h-6 w-auto" />
            </div>
          )}
          <p className="text-xs text-neutral-500">{brand.company || 'Next Experience Group'} — {formatDate(audit.createdAt)}</p>
        </div>
      </div>
    </div>
  );
}
